import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ISdVoteDto } from '../../../shared/models/sd/sd-vote';
import { SdVoteId, sdVoteOption } from '../../../../../shared/constants/sd-ballot.constants';
import { Country } from '../../../../../shared/constants/srs-ballot.constants';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { AuthService } from '../../../../../shared/services/auth.service';
import { IDonutChartItem } from '../../../../controls/donut-chart/donut-chart.component';
import { ISdConsensusCriteria } from '../../../shared/models/sd/sd-consensus-criteria';
import { SdBallotService } from '../../../shared/services/sd-ballot.service';
import { SdFinaliseBallotModalComponent } from '../sd-finalise-ballot-modal/sd-finalise-ballot-modal.component';


@Component({
    selector: 'app-sd-eligible-voters',
    templateUrl: './sd-eligible-voters.component.html',
    styleUrl: './sd-eligible-voters.component.scss',
    standalone: false
})
export class SdEligibleVotersComponent implements OnChanges {
  @Input() ballotVotes: ISdVoteDto[] = [];
  @Input() isJoint: boolean = false;
  @Input() sdBallotStatus?: UnifiedBallotStatus;
  @Input() projectId: string = '';
  @Input() designation: string = '';
  @Output() onFinalisedBallot: EventEmitter<number> = new EventEmitter<number>();

  isShowingLocations: boolean = false;
  data: IDonutChartItem[] = [];
  consensusData: IDonutChartItem[] = [];
  eligibleVotes: ISdVoteDto[] = [];
  agreeCount: number = 0;
  disagreeCount: number = 0;
  noneCount: number = 0;
  selectedLocation: string = 'aus-nz';
  ballotType = 'srsBallot';
  isConsensusVisible: boolean = false;
  isRefresh: boolean = true;
  isUserPmOrAboveOrHoSD: boolean = false;
  isValidStatusGenerateReport: boolean = false;
  isFinaliseButtonVisible: boolean = false;


  voteTypes = [
    SdVoteId.VT_AGREE,
    SdVoteId.VT_DISAGREE,
    SdVoteId.VT_NONE,
  ];


  constructor(
    public sdBallotService: SdBallotService,
    public authService: AuthService,
    private _modalService: NgbModal) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ballotVotes'] && changes['ballotVotes'].currentValue) {
      this.ballotVotes = changes['ballotVotes'].currentValue;

      if (this.ballotVotes.length > 0) {
        this.checkConsensusVisible();
        this.filterEligibleVotes();
        this.getVoteDetails();
        this.checkNZCommittee();
        this.checkUserPermission();
        this.checkSdBallotStatus();
      }
    }
  }

  checkUserPermission() {
    this.authService.isUserPmOrAboveOrHoSD().subscribe({
      next: (isAuthorized) => {
        this.isUserPmOrAboveOrHoSD = isAuthorized;
      },
      error: (err) => {
        this.isUserPmOrAboveOrHoSD = false;
        console.error('Error checking authorisation:', err);
      }
    });
  }

  checkSdBallotStatus(){
    this.isValidStatusGenerateReport = this.sdBallotStatus === UnifiedBallotStatus.openForVoting || this.sdBallotStatus === UnifiedBallotStatus.closedForVoting || this.sdBallotStatus === UnifiedBallotStatus.finalised;
  }

  getVoteDetails() {
    const voteCounts = this.getVoteCount(this.eligibleVotes);
    const voteWeights = this.getVoteWeight(this.eligibleVotes);
    // Assign counts to your specific variables
    this.agreeCount = voteCounts[SdVoteId.VT_AGREE] || 0;
    this.disagreeCount = voteCounts[SdVoteId.VT_DISAGREE] || 0;
    this.noneCount = voteCounts[SdVoteId.VT_NONE] || 0;

    if (voteCounts) {
      this.generateDonutChart(voteCounts, voteWeights);

      this.setConsensusData(voteCounts, voteWeights);
    }
  }

  filterByLocation(): void {
    const filteredVotes = this.eligibleVotes.filter((vote) => {
      const voteCountry = vote.country.toLowerCase();
      if (this.selectedLocation === 'aus-nz') {
        this.isRefresh = true;
        return (
          Country.Australia.includes(voteCountry) ||
          Country.NewZealand.includes(voteCountry)
        );
      } else if (this.selectedLocation === 'aus') {
        this.isRefresh = true;
        return Country.Australia.includes(voteCountry);
      } else if (this.selectedLocation === 'nz') {
        this.isRefresh = false;
        return Country.NewZealand.includes(voteCountry);
      }

      return false;
    });

    const voteCounts = this.getVoteCount(filteredVotes);

    const voteWeights = this.getVoteWeight(filteredVotes);

    this.generateDonutChart(voteCounts, voteWeights);


  }

  getVoteCount(votes: ISdVoteDto[]) {
    const voteCounts = votes.reduce(
      (counts: { [key: string]: number }, vote) => {
        const type = vote.voteId;
        if (type) {
          counts[type] = (counts[type] || 0) + 1;
        }
        return counts;
      },
      {}
    );

    return voteCounts;
  }

  getVoteWeight(votes: ISdVoteDto[]) {
    const voteWeights = votes.reduce(
      (counts: { [key: string]: number }, vote) => {
        const type = vote.voteId;
        if (type) {
          counts[type] = (counts[type] || 0) + (vote?.weight??1);
        }
        return counts;
      },
      {}
    );

    return voteWeights;
  }

  generateDonutChart(voteCounts: any, voteWeights: any): void {

    this.data = this.voteTypes.map((voteType) => ({
      label: sdVoteOption[voteType].text,
      value: voteCounts[voteType] || 0,
      weight: voteWeights[voteType] || 0,
      color: sdVoteOption[voteType].color,
      icon: sdVoteOption[voteType].icon,
    }));

    //this.cdr.detectChanges();
  }


  setConsensusData(voteCounts: any, voteWeights: any): void {

    this.consensusData = this.voteTypes.map((voteType) => ({
      label: sdVoteOption[voteType].text,
      value: voteCounts[voteType] || 0,
      weight: voteWeights[voteType] || 0,
      color: sdVoteOption[voteType].color,
      icon: sdVoteOption[voteType].icon,
    }));

  }

  filterEligibleVotes() {
    // Filter votes based on eligibility
    this.eligibleVotes = this.ballotVotes.filter((vote) =>
      vote.isEligible
    );
  }

  checkNZCommittee() {
    const hasNZCommitteeVotes = this.ballotVotes.find(
      (vote) =>
        vote.country && Country.NewZealand.includes(vote.country.toLowerCase())
    );
    this.isShowingLocations = hasNZCommitteeVotes != null && this.isJoint;
  }

  checkConsensusVisible() {
    this.isConsensusVisible =
      this.sdBallotStatus == UnifiedBallotStatus.openForVoting
      || this.sdBallotStatus == UnifiedBallotStatus.closedForVoting
      ;
  }


  generateSDBallotReport() {
    this.sdBallotService.downloadBallotReport(this.projectId).subscribe(blob => {
      const fileName = `${this.designation} Ballot Report.xlsx`;
      const newBlob = new Blob([blob], { type: 'application/xlsx' });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(newBlob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  }

  onConsensusChanged(consensusCriteria: ISdConsensusCriteria) {
    this.checkFinaliseButtonVisible(consensusCriteria);
  }

  checkFinaliseButtonVisible(consensusCriteria: ISdConsensusCriteria) {
     this.isFinaliseButtonVisible = (this.isUserPmOrAboveOrHoSD
        && this.sdBallotStatus == UnifiedBallotStatus.closedForVoting
        && consensusCriteria.hasMetSimpleMajorityConsensus
        && consensusCriteria.hasMetFullConsensus
    )
  }

  onFinaliseBallotClicked() {
    const modalRef = this._modalService.open(SdFinaliseBallotModalComponent);
    modalRef.componentInstance.ballotId = this.ballotVotes[0].ballotRecordId;
    modalRef.componentInstance.projectId = this.projectId;

    modalRef.result.then(
      (result: number) => {
        if (result) {
          this.onFinalisedBallot.emit(result);
        }
      });
  }
}
